"use client";

import * as React from "react";
import { type ThemeId, defaultThemeId, themeIds, themes } from "@smallbiz/design-tokens";
import {
  applyTheme,
  getCurrentTheme,
  isDarkMode,
  setDarkMode,
  toggleDarkMode,
  initializeTheme,
} from "./utilities";

// ============================================================================
// CONTEXT
// ============================================================================

interface ThemeContextValue {
  /** Current active theme ID */
  theme: ThemeId;
  /** Set the active theme */
  setTheme: (theme: ThemeId) => void;
  /** Whether dark mode is active */
  isDark: boolean;
  /** Toggle dark mode on/off */
  toggleDark: () => void;
  /** Set dark mode explicitly */
  setDark: (enabled: boolean) => void;
  /** List of all available theme IDs */
  themeIds: readonly ThemeId[];
  /** Theme metadata for all themes */
  themes: typeof themes;
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export interface ThemeProviderProps {
  /** Child components */
  children: React.ReactNode;
  /** Default theme to use if none is stored */
  defaultTheme?: ThemeId;
  /** Whether to initialize from localStorage on mount */
  storageEnabled?: boolean;
  /** Attribute to set on the root element */
  attribute?: "class" | "data-theme";
  /** Enable system color scheme preference detection */
  enableSystem?: boolean;
}

/**
 * ThemeProvider - React context provider for theme management
 *
 * Provides theme state and methods to child components via useTheme hook.
 *
 * @example
 * ```tsx
 * import { ThemeProvider } from '@smallbiz/brand-kit-themes';
 *
 * function App() {
 *   return (
 *     <ThemeProvider defaultTheme="blue-momentum">
 *       <YourApp />
 *     </ThemeProvider>
 *   );
 * }
 * ```
 */
export function ThemeProvider({
  children,
  defaultTheme = defaultThemeId,
  storageEnabled = true,
  enableSystem = true,
}: ThemeProviderProps) {
  const [theme, setThemeState] = React.useState<ThemeId>(defaultTheme);
  const [isDark, setIsDarkState] = React.useState(false);

  // Initialize theme from storage on mount
  React.useEffect(() => {
    if (storageEnabled) {
      initializeTheme();
      setThemeState(getCurrentTheme());
      setIsDarkState(isDarkMode());
    } else {
      applyTheme(defaultTheme);
    }
  }, [defaultTheme, storageEnabled]);

  // Listen for system color scheme changes
  React.useEffect(() => {
    if (!enableSystem || typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-update if no explicit preference is stored
      const stored = localStorage.getItem("sbi-dark-mode");
      if (!stored) {
        setDarkMode(e.matches);
        setIsDarkState(e.matches);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [enableSystem]);

  const setTheme = React.useCallback((newTheme: ThemeId) => {
    applyTheme(newTheme);
    setThemeState(newTheme);
  }, []);

  const toggleDark = React.useCallback(() => {
    const newState = toggleDarkMode();
    setIsDarkState(newState);
  }, []);

  const setDark = React.useCallback((enabled: boolean) => {
    setDarkMode(enabled);
    setIsDarkState(enabled);
  }, []);

  const value = React.useMemo(
    () => ({
      theme,
      setTheme,
      isDark,
      toggleDark,
      setDark,
      themeIds,
      themes,
    }),
    [theme, setTheme, isDark, toggleDark, setDark],
  );

  // Render immediately to prevent blank page on initial load (CSR-only app, no SSR hydration).
  // Before mount, children render with `defaultTheme` and `isDark = false`. After the first
  // useEffect, stored preferences are applied — users with a non-default theme may see a brief
  // flash. This is intentional: a flash of the wrong theme is far preferable to a blank page.
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * useTheme - Hook to access theme context
 *
 * @example
 * ```tsx
 * import { useTheme } from '@smallbiz/brand-kit-themes';
 *
 * function ThemeSwitcher() {
 *   const { theme, setTheme, isDark, toggleDark, themeIds } = useTheme();
 *
 *   return (
 *     <div>
 *       <select value={theme} onChange={(e) => setTheme(e.target.value as ThemeId)}>
 *         {themeIds.map(id => (
 *           <option key={id} value={id}>{id}</option>
 *         ))}
 *       </select>
 *       <button onClick={toggleDark}>
 *         {isDark ? 'Light Mode' : 'Dark Mode'}
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useTheme(): ThemeContextValue {
  const context = React.useContext(ThemeContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
}

// ============================================================================
// EXPORTS
// ============================================================================

export { ThemeContext };
export type { ThemeContextValue };
